import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Loader, BookOpen, Calculator, Briefcase,
  Users, Sprout, Award, Heart, Star, Tv, AlertCircle, RefreshCw,
  GraduationCap, ChevronRight, BookMarked, ShoppingCart, CheckCircle, Lock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useTVNavigation } from '../hooks/useTVNavigation';

// ─── Constants ────────────────────────────────────────────────────
const isTV = () => Capacitor.isNativePlatform();

const K12_BASE           = 'https://k12.easylearn.org.in/Easylearn';
const K12_CHECK_REGISTER = `${K12_BASE}/Course_Controller/athenastar_check_or_register`;
const K12_LOGIN          = `${K12_BASE}/Register_Controller/login_user_mobile`;
const K12_COURSES        = `${K12_BASE}/Course_Controller/enrolledcourse_details_getdata_android`;

// K12 POST helper — x-www-form-urlencoded
const k12Post = async (url, params) => {
  const res = await fetch(url, {
    method:  'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body:    new URLSearchParams(params).toString(),
  });
  return res.json();
};

// ─── Grade list 1–12 ──────────────────────────────────────────────
const GRADES = Array.from({ length: 12 }, (_, i) => ({
  id:    i + 1,
  label: i === 0 ? '1st' : i === 1 ? '2nd' : i === 2 ? '3rd' : `${i + 1}th`,
  color: [
    'from-blue-500 to-blue-700',
    'from-teal-500 to-teal-700',
    'from-purple-500 to-purple-700',
    'from-orange-500 to-orange-700',
    'from-pink-500 to-pink-700',
    'from-indigo-500 to-indigo-700',
    'from-green-500 to-green-700',
    'from-red-500 to-red-700',
    'from-yellow-500 to-yellow-700',
    'from-cyan-500 to-cyan-700',
    'from-rose-500 to-rose-700',
    'from-emerald-500 to-emerald-700',
  ][i],
}));

// ─── Education categories ─────────────────────────────────────────
const educationCategories = [
  { id: 2, name: 'K12 Education',       description: 'School curriculum & resources',     icon: BookOpen,   color: 'from-blue-500 to-blue-700',      website: 'https://k12.easylearn.org.in/',           image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=800', isK12: true  },
  { id: 3, name: 'Mathematics',         description: 'Advanced math concepts & practice', icon: Calculator, color: 'from-teal-500 to-purple-900',    website: 'http://mathematics.easylearn.net.in/',    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&w=800', isK12: false },
  { id: 4, name: 'Vocational Training', description: 'Skill development programs',        icon: Briefcase,  color: 'from-green-500 to-green-700',   website: 'http://vocational.skillmaster.net.in/',   image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800', isK12: false },
  { id: 5, name: 'Industry Skills',     description: 'Professional development courses',  icon: Users,      color: 'from-orange-500 to-orange-700', website: 'https://midc.easylearn.org.in/',          image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800', isK12: false },
  { id: 6, name: 'Agriculture',         description: 'Farming & agricultural sciences',   icon: Sprout,     color: 'from-emerald-500 to-emerald-700',website: 'http://agriculture.skillmaster.net.in/',  image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=800', isK12: false },
  { id: 7, name: 'UPSC Preparation',    description: 'Civil services exam resources',     icon: Award,      color: 'from-red-500 to-red-700',       website: 'https://upsc.skillmaster.net.in/',        image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&w=800', isK12: false },
  { id: 8, name: 'Medical Sciences',    description: 'Healthcare & medical education',    icon: Heart,      color: 'from-pink-500 to-pink-700',     website: 'https://medical.skillmaster.net.in',      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800', isK12: false },
];

// ═════════════════════════════════════════════════════════════════
// Main Education Component
// ═════════════════════════════════════════════════════════════════
const Education = ({ user }) => {
  const navigate = useNavigate();

  // Web state
  const [isLoading,       setIsLoading]       = useState(false);
  const [selectedWebsite, setSelectedWebsite] = useState(null);

  // TV state
  // screen: 'categories' | 'checking' | 'purchase' | 'purchasing' |
  //         'grades' | 'loading' | 'courses' | 'error'
  const [tvScreen,      setTvScreen]      = useState('categories');
  const [tvError,       setTvError]       = useState('');
  const [courses,       setCourses]       = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [k12UserId,     setK12UserId]     = useState(null); // K12 user id after check/register

  // D-pad back navigation
  useTVNavigation({
    enabled: true,
    onBack: () => {
      if (tvScreen === 'checking')   { setTvScreen('categories'); return; }
      if (tvScreen === 'purchase')   { setTvScreen('categories'); return; }
      if (tvScreen === 'purchasing') { return; } // block back during purchase
      if (tvScreen === 'grades')     { setTvScreen('categories'); return; }
      if (tvScreen === 'courses')    { setTvScreen('grades');     return; }
      if (tvScreen === 'error')      { setTvScreen('categories'); return; }
      navigate('/');
    },
  });

  // ── Step 1: K12 card click → check email ──────────────────────
  const handleK12Click = async () => {
    if (!user?.email) {
      setTvError('User email not found. Please logout and login again.');
      setTvScreen('error');
      return;
    }

    setTvScreen('checking');
    setTvError('');

    try {
      const res = await k12Post(K12_CHECK_REGISTER, {
        email: user.email,
        name:  user.name ?? '',
      });

      if (res.Response === 'FOUND') {
        // User already exists in K12 → direct to grade selection
        setK12UserId(res.user_id);
        setTvScreen('grades');
      } else if (res.Response === 'CREATED') {
        // Should not reach here — purchase screen was skipped on FOUND
        // But handle gracefully
        setK12UserId(res.user_id);
        setTvScreen('grades');
      } else if (res.Response === 'NOT_FOUND') {
        // User not in K12 → show purchase screen
        setTvScreen('purchase');
      } else {
        // NEW_USER or any non-FOUND → show purchase screen
        setTvScreen('purchase');
      }
    } catch (e) {
      setTvError('Could not connect to K12. Please check your connection.');
      setTvScreen('error');
    }
  };

  // ── Step 2: Dummy purchase → register in K12 ──────────────────
  const handlePurchaseConfirm = async () => {
    setTvScreen('purchasing');
    setTvError('');

    try {
      const res = await k12Post(K12_CHECK_REGISTER, {
        email: user.email,
        name:  user.name ?? '',
      });

      if (res.Response === 'FOUND' || res.Response === 'CREATED') {
        setK12UserId(res.user_id);
        setTvScreen('grades');
      } else {
        throw new Error(res.message || 'Registration failed');
      }
    } catch (e) {
      setTvError(e.message || 'Purchase failed. Please try again.');
      setTvScreen('error');
    }
  };

  // ── Step 3: Grade select → login + fetch courses ───────────────
  const handleGradeSelect = async (grade) => {
    setSelectedGrade(grade);
    setTvScreen('loading');
    setTvError('');

    try {
      // K12 grade-based demo login
      const loginData = await k12Post(K12_LOGIN, {
        grade:      grade.id,
        profile_id: k12UserId ?? user?.id ?? '',
      });

      if (!loginData || loginData.Response !== 'OK') {
        throw new Error(loginData?.data || 'K12 login failed');
      }

      const session = loginData.data;

      // Fetch enrolled courses
      const courseData = await k12Post(K12_COURSES, {
        user_id:    session.user_id  ?? session.id  ?? '',
        auth_key:   session.auth_key ?? session.token ?? '',
        profile_id: k12UserId ?? user?.id ?? '',
      });

      const list = Array.isArray(courseData?.data)
        ? courseData.data
        : Array.isArray(courseData)
        ? courseData
        : [];

      setCourses(list);
      setTvScreen('courses');
    } catch (e) {
      setTvError(e.message || 'Could not load courses. Please try again.');
      setTvScreen('error');
    }
  };

  // ── Web card click ─────────────────────────────────────────────
  const handleWebCardClick = (category) => {
    setSelectedWebsite(category);
    setIsLoading(true);
    navigate('/education/web');
  };

  const handleBack = () => {
    setSelectedWebsite(null);
    setIsLoading(false);
    navigate('/education');
  };

  const isIframePage = window.location.pathname === '/education/web';

  // ══════════════════════════════════════════════════════════════
  // TV SCREENS
  // ══════════════════════════════════════════════════════════════
  if (isTV()) {

    // ── Checking email in K12 ──────────────────────────────────
    if (tvScreen === 'checking') {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <p className="text-white text-2xl font-semibold">Checking your account…</p>
            <p className="text-gray-400 mt-2">Please wait</p>
          </div>
        </div>
      );
    }

    // ── Dummy Purchase Screen ──────────────────────────────────
    if (tvScreen === 'purchase') {
      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black flex items-center justify-center">
          <div className="max-w-lg w-full mx-8">

            {/* Card */}
            <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden shadow-2xl">

              {/* Top gradient strip */}
              <div className="h-2 bg-gradient-to-r from-blue-500 to-teal-500" />

              <div className="p-8">
                {/* Icon */}
                <div className="w-16 h-16 bg-blue-500/15 border border-blue-500/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <ShoppingCart className="w-8 h-8 text-blue-400" />
                </div>

                <h2 className="text-white text-2xl font-bold text-center mb-2">
                  K12 Education
                </h2>
                <p className="text-gray-400 text-center text-sm mb-8">
                  Get access to school curriculum, courses & resources for Classes 1–12
                </p>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {[
                    'Access to all Class 1–12 courses',
                    'Subject-wise learning material',
                    'Video lessons & practice topics',
                    'Progress tracking across grades',
                  ].map((feat, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0" />
                      <span className="text-gray-300 text-sm">{feat}</span>
                    </div>
                  ))}
                </div>

                {/* Price */}
                <div className="bg-gray-900/60 rounded-xl px-6 py-4 text-center mb-6 border border-gray-700">
                  <p className="text-gray-400 text-xs uppercase tracking-widest mb-1">Plan</p>
                  <p className="text-white text-3xl font-extrabold">FREE</p>
                  <p className="text-gray-500 text-xs mt-1">Full access included with your account</p>
                </div>

                {/* Buttons */}
                <button
                  onClick={handlePurchaseConfirm}
                  className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white font-bold text-lg py-4 rounded-xl hover:opacity-90 transition mb-3"
                >
                  Activate K12 Access
                </button>
                <button
                  onClick={() => setTvScreen('categories')}
                  className="w-full bg-gray-700 text-gray-300 font-semibold text-base py-3 rounded-xl hover:bg-gray-600 transition"
                >
                  Go Back
                </button>
              </div>
            </div>

            {/* User info */}
            <p className="text-gray-600 text-xs text-center mt-4">
              Activating for: {user?.email}
            </p>
          </div>
        </div>
      );
    }

    // ── Purchasing / Registering ───────────────────────────────
    if (tvScreen === 'purchasing') {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <p className="text-white text-2xl font-semibold">Activating your K12 access…</p>
            <p className="text-gray-400 mt-2">Please wait</p>
          </div>
        </div>
      );
    }

    // ── Loading courses ────────────────────────────────────────
    if (tvScreen === 'loading') {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <p className="text-white text-2xl font-semibold">
              Loading Class {selectedGrade?.label} Courses…
            </p>
            <p className="text-gray-400 mt-2">Please wait</p>
          </div>
        </div>
      );
    }

    // ── Error ──────────────────────────────────────────────────
    if (tvScreen === 'error') {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center max-w-lg px-8">
            <AlertCircle className="w-20 h-20 text-red-400 mx-auto mb-6" />
            <h2 className="text-white text-2xl font-bold mb-4">Something Went Wrong</h2>
            <p className="text-gray-300 mb-8">{tvError}</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => { setTvScreen('categories'); setTvError(''); }}
                className="flex items-center gap-2 bg-teal-500 text-white px-8 py-3 rounded-xl font-semibold text-lg hover:bg-teal-400 transition"
              >
                <RefreshCw className="w-5 h-5" /> Retry
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 bg-gray-700 text-white px-8 py-3 rounded-xl font-semibold text-lg hover:bg-gray-600 transition"
              >
                <ArrowLeft className="w-5 h-5" /> Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ── Grade Selection ────────────────────────────────────────
    if (tvScreen === 'grades') {
      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
          <div className="px-8 pb-8 max-w-7xl mx-auto" style={{ paddingTop: 90 }}>

            {/* Header */}
            <div className="mb-8 flex items-center gap-4">
              <button
                onClick={() => setTvScreen('categories')}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-xl transition border border-gray-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-2xl px-6 py-4 flex items-center gap-4 flex-1">
                <GraduationCap className="w-8 h-8 text-teal-400 flex-shrink-0" />
                <div>
                  <p className="text-white font-semibold text-lg">K12 Education</p>
                  <p className="text-gray-400 text-sm">Select your class to view courses</p>
                </div>
              </div>
            </div>

            {/* Grade Grid */}
            <div data-tv-grid className="grid grid-cols-4 lg:grid-cols-6 gap-4">
              {GRADES.map((grade) => (
                <div
                  key={grade.id}
                  data-tv-card
                  tabIndex={0}
                  onClick={() => handleGradeSelect(grade)}
                  onKeyDown={e => e.key === 'Enter' && handleGradeSelect(grade)}
                  className="group relative rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-105 focus:scale-105 hover:shadow-2xl border border-gray-700 hover:border-teal-500 focus:border-teal-500 outline-none"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${grade.color} opacity-80`} />
                  <div className="relative p-5 h-28 flex flex-col items-center justify-center text-center">
                    <span className="text-3xl font-extrabold text-white leading-none">{grade.label}</span>
                    <span className="text-white/70 text-xs mt-1 font-medium">Class</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // ── Courses List ───────────────────────────────────────────
    if (tvScreen === 'courses') {
      const gradientColors = [
        'from-blue-600 to-blue-900',
        'from-teal-600 to-teal-900',
        'from-purple-600 to-purple-900',
        'from-orange-600 to-orange-900',
        'from-green-600 to-green-900',
        'from-pink-600 to-pink-900',
      ];

      return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
          <div className="px-8 pb-8 max-w-7xl mx-auto" style={{ paddingTop: 90 }}>

            {/* Header */}
            <div className="mb-8 flex items-center gap-4">
              <button
                onClick={() => setTvScreen('grades')}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-xl transition border border-gray-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-2xl px-6 py-4 flex items-center gap-4 flex-1">
                <BookMarked className="w-8 h-8 text-teal-400 flex-shrink-0" />
                <div>
                  <p className="text-white font-semibold text-lg">
                    Class {selectedGrade?.label} — Courses
                  </p>
                  <p className="text-gray-400 text-sm">
                    {courses.length} course{courses.length !== 1 ? 's' : ''} available
                  </p>
                </div>
              </div>
            </div>

            {/* Empty state */}
            {courses.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <BookOpen className="w-16 h-16 text-gray-600 mb-4" />
                <p className="text-gray-400 text-xl">
                  No courses found for Class {selectedGrade?.label}
                </p>
                <button
                  onClick={() => setTvScreen('grades')}
                  className="mt-6 bg-teal-500 text-white px-8 py-3 rounded-xl font-semibold hover:bg-teal-400 transition"
                >
                  Try Another Class
                </button>
              </div>
            )}

            {/* Course Cards */}
            {courses.length > 0 && (
              <div data-tv-grid className="grid grid-cols-2 lg:grid-cols-3 gap-5">
                {courses.map((course, idx) => (
                  <div
                    key={course.course_id ?? course.id ?? idx}
                    data-tv-card
                    tabIndex={0}
                    className="group relative bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-teal-500 focus:border-teal-500 outline-none transition-all duration-300 hover:scale-[1.02] focus:scale-[1.02] hover:shadow-2xl"
                  >
                    {/* Color strip */}
                    <div className={`h-2 bg-gradient-to-r ${gradientColors[idx % gradientColors.length]}`} />

                    <div className="p-5">
                      {/* Badges */}
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        {course.subject && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-400 border border-teal-500/25">
                            {course.subject}
                          </span>
                        )}
                        {course.medium && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-700 text-gray-300 border border-gray-600">
                            {course.medium}
                          </span>
                        )}
                      </div>

                      {/* Name */}
                      <h3 className="text-white font-bold text-base leading-snug mb-1 line-clamp-2">
                        {course.course_name ?? course.name ?? `Course ${idx + 1}`}
                      </h3>

                      {/* Description */}
                      {course.description && (
                        <p className="text-gray-400 text-xs leading-relaxed line-clamp-2 mt-1">
                          {course.description}
                        </p>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-700">
                        <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                          <BookOpen className="w-3.5 h-3.5" />
                          <span>{course.total_topics ?? course.topic_count ?? '—'} topics</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-teal-400 transition-colors" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    // ── Category Cards (default TV screen) ────────────────────
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
        <div className="px-8 pb-8 max-w-7xl mx-auto" style={{ paddingTop: 90 }}>

          {/* Banner */}
          <div className="mb-8 flex items-center gap-4 bg-teal-500/10 border border-teal-500/30 rounded-2xl px-6 py-4">
            <Tv className="w-8 h-8 text-teal-400 flex-shrink-0" />
            <div>
              <p className="text-white font-semibold text-lg">EasyLearn Education</p>
              <p className="text-gray-400 text-sm">Select a category to explore content</p>
            </div>
            {user?.name && (
              <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/20 text-teal-300 text-sm font-semibold">
                {user.name}
              </div>
            )}
          </div>

          <div data-tv-grid className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {educationCategories.map((cat) => {
              const Icon = cat.icon;
              return (
                <div
                  key={cat.id}
                  data-tv-card
                  tabIndex={cat.isK12 ? 0 : -1}
                  onClick={() => cat.isK12 && handleK12Click()}
                  onKeyDown={e => e.key === 'Enter' && cat.isK12 && handleK12Click()}
                  className={`group relative bg-gray-800 rounded-xl overflow-hidden transition-all duration-300 border border-gray-700 outline-none
                    ${cat.isK12
                      ? 'cursor-pointer hover:scale-105 focus:scale-105 hover:shadow-2xl hover:border-teal-500 focus:border-teal-500'
                      : 'opacity-40 cursor-not-allowed'
                    }`}
                >
                  <div className="absolute inset-0">
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity" />
                    <div className={`absolute inset-0 bg-gradient-to-br ${cat.color} opacity-60`} />
                  </div>
                  <div className="relative p-4 h-36 flex flex-col justify-between">
                    <div>
                      <Icon className="w-7 h-7 mb-2 text-white" strokeWidth={1.5} />
                      <h3 className="text-sm font-bold text-white mb-0.5 leading-tight">{cat.name}</h3>
                      <p className="text-gray-200 text-xs leading-snug">{cat.description}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-white/70">
                        {cat.isK12 ? 'Select Class' : 'Coming Soon'}
                      </span>
                      {cat.isK12
                        ? <ChevronRight className="w-4 h-4 text-white/60" />
                        : <Lock className="w-3.5 h-3.5 text-white/30" />
                      }
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // WEB: iframe view
  // ══════════════════════════════════════════════════════════════
  if (isIframePage) {
    return (
      <div className="min-h-screen bg-gray-900">
        <button
          onClick={handleBack}
          className="fixed top-4 left-4 z-50 flex items-center space-x-2 bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-black/90 transition shadow-lg"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-semibold">Back</span>
        </button>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-40">
            <div className="text-center">
              <Loader className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
              <p className="text-white text-lg">Loading {selectedWebsite?.name}…</p>
            </div>
          </div>
        )}
        <iframe
          src={selectedWebsite?.website}
          onLoad={() => setIsLoading(false)}
          title={selectedWebsite?.name}
          className="w-full h-screen border-0"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-top-navigation"
          loading="lazy"
        />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════
  // WEB: Category Cards grid (unchanged)
  // ══════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
      <div className="px-8 pb-16 max-w-7xl mx-auto" style={{ paddingTop: 120 }}>
        <div data-tv-grid className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {educationCategories.map((cat) => {
            const Icon = cat.icon;
            return (
              <div
                key={cat.id}
                data-tv-card
                tabIndex={0}
                onClick={() => handleWebCardClick(cat)}
                onKeyDown={e => e.key === 'Enter' && handleWebCardClick(cat)}
                className="group relative bg-gray-800 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl border border-gray-700 hover:border-teal-500 focus:border-teal-500 focus:outline-none focus:scale-105"
              >
                <div className="absolute inset-0">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${cat.color} opacity-60`} />
                </div>
                <div className="relative p-6 h-64 flex flex-col justify-between">
                  <div>
                    <Icon className="w-12 h-12 mb-4 text-white" strokeWidth={1.5} />
                    <h3 className="text-xl font-bold mb-2 text-white">{cat.name}</h3>
                    <p className="text-gray-200 text-sm">{cat.description}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-white/80">Click to explore</span>
                    <ArrowLeft className="w-5 h-5 text-white rotate-180 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Education;